import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '@store/auth/auth.slice';
import tw, { theme } from 'twin.macro';
import LanguageSelector from '@components/elements/LanguageSelector/LanguageSelector';
import DisplayUpLg from '@components/elements/DisplayOn/DisplayUpLg';
import DisplayDownLg from '@components/elements/DisplayOn/DisplayDownLg';
import type { CSSObjectWithLabel, DropdownIndicatorProps } from 'react-select';
import useAppConfig from '@hooks/useAppConfig';

import Logo from './Logo';
import AuthNav from './AuthNav';
import MoneyBoxBalance from './MoneyBoxBalance/MoneyBoxBalance';
import OrderLinks from './OrderLinks';

const HamburgerMenu = dynamic(() => import('./HamburgerMenu/HamburgerMenu'));

const languageSelectorStyles = {
  control: () => ({
    display: 'flex',
    padding: '0',
    minWidth: '90px',
    cursor: 'pointer',
    fontSize: '0.875rem',
  }),
  singleValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    fontSize: '0.875rem',
  }),
  dropdownIndicator: (
    provided: CSSObjectWithLabel,
    { selectProps }: DropdownIndicatorProps
  ) => ({
    ...provided,
    padding: '8px 0',
    transform: selectProps.menuIsOpen ? 'rotate(180deg)' : null,
  }),
  indicatorSeparator: () => ({}),
  option: (provided: CSSObjectWithLabel) => ({
    ...provided,
    padding: '8px 10px',
    fontSize: '0.875rem',
    '&:hover': {
      cursor: 'pointer',
      color: theme`colors.white`,
    },
  }),
  menuPortal: (provided: CSSObjectWithLabel) => ({ ...provided, zIndex: 99 }),
};

const Header = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const {
    data: {
      multinational: { clientAllowedToChangeLanguage },
    },
  } = useAppConfig();

  return (
    <header
      id="header"
      tw="py-2.5 bg-white border-b border-gray-1 h-20 flex items-center"
      data-cy="Header_header_header"
    >
      <div className="container">
        <DisplayUpLg>
          <div className="row" tw="items-center justify-between">
            <div className="col-auto">
              <div tw="flex items-center">
                <Logo />
                <OrderLinks secondaryButtonVariant="text" />
              </div>
            </div>
            <div className="col-auto">
              <div
                css={[
                  tw`flex items-center`,
                  isAuthenticated ? tw`-mx-2` : tw`-ml-2 -mr-3`,
                ]}
              >
                {isAuthenticated && (
                  <MoneyBoxBalance styles={{ link: { css: tw`mx-2` } }} />
                )}
                {clientAllowedToChangeLanguage && (
                  <div tw="mx-2">
                    <LanguageSelector customStyles={languageSelectorStyles} />
                  </div>
                )}
                <AuthNav />
              </div>
            </div>
          </div>
        </DisplayUpLg>

        <DisplayDownLg>
          <div tw="flex items-center">
            <Logo />
          </div>
          <HamburgerMenu />
        </DisplayDownLg>
      </div>
    </header>
  );
};

export default Header;
